import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import axios from "axios";
import { useTheme } from "../../../../components/hooks/useTheme";
import useAuth from "../../../../components/hooks/useAuth";
import baseURL from "../../../../configs/api";
import PleaseWaitPopup from "../../../PleaseWaitPopup";
import { CustomDataGrid } from "../../../../components/common/CustomDataGrid";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import useDelete from "../../../../components/hooks/useDelete";
import { SendEmailChecklist } from "./SendEmailChecklist";
import moment from 'moment';
import CustomFormatDatePicker from "../../../../components/common/CustomFormatDatePicker";
import { PreviewPopup } from "./PreviewPopup";
import colorConfigs from "../../../../configs/colorConfigs";
import useGeneral from "../../../../components/hooks/useGeneral";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";

type Props = {
    idNumber: any;
};

type VehicleChecklistRow = {
    id: number;
    checklistName: string;
    filePath?: string;
    uploadedBy?: string;
    uploadedAt?: string;
    expirationDate?: string;
    isRequired?: boolean;
    isUploaded?: boolean;
    tags: string;
    description: string;
};

export const VehicleChecklist = ({ idNumber }: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const { setDeletePopup, setData }: any = useDelete();
    const [tableData, setTableData] = useState<VehicleChecklistRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState(false);
    const [isFilesRefresh, setIsFileRefresh] = useState(true);
    const [openSendEmailPopup, setOpenSendEmailPopup] = useState(false);
    const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<VehicleChecklistRow | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [expirationDateInput, setExpirationDateInput] = useState<string>("");
    const [imagePopup, setImagePopup] = useState(false);
    const [previewFilePath, setPreviewFilePath] = useState<string>("");
    const [fileNameForEmail, setFileNameForEmail] = useState<string>("");

    const columns: GridColDef[] = [
        {
            field: "checklistName",
            headerName: "Filename",
            flex: 1.5,
            align: "left",
            editable: false,
            sortable: false,
            renderCell: (params) => {
                const item = params.row;
                let statusColor = "bg-red-500";
                if (!item.isRequired) {
                    statusColor = "bg-yellow-500";
                }
                if (
                    item.isRequired &&
                    item.isUploaded &&
                    item.expirationDate &&
                    moment(item.expirationDate, dateFormat).isSameOrAfter(moment(), "day")
                ) {
                    statusColor = "bg-green-600";
                }

                const hasFile = !!item.filePath;

                return (
                    <div className="flex items-center gap-2">
                        <span
                            className={`w-2.5 h-2.5 rounded-full ${statusColor}`}
                            title={
                                !item.isRequired
                                    ? "Not Required"
                                    : item.isUploaded && item.expirationDate
                                        ? "OK"
                                        : "Not OK"
                            }
                        />
                        <span
                            onClick={() => {
                                if (hasFile) {
                                    setSelectedRow(item);
                                    setPreviewFilePath(item.filePath);
                                    setImagePopup(true);
                                }
                            }}
                            className={hasFile ? "underline text-[#0000ff] cursor-pointer" : ""}
                        >
                            {params?.value}
                        </span>
                    </div>
                );
            },
        },
        {
            field: "folder",
            headerName: "Folder",
            flex: 1,
            align: "left",
            editable: false,
            sortable: false,
        },
        {
            field: "isRequired",
            headerName: "Required",
            flex: 0.5,
            align: "left",
            editable: false,
            sortable: false,
            renderCell: (params) => (
                <span>
                    {params.value ? "Yes" : "No"}
                </span>
            ),
        },
        {
            field: "isUploaded",
            headerName: "Uploaded",
            flex: 0.5,
            align: "left",
            editable: false,
            sortable: false,
            renderCell: (params) => (
                <span>
                    {params.value ? "Yes" : "No"}
                </span>
            ),
        },
        {
            field: "expirationDate",
            headerName: "Expiration Date",
            flex: 1,
            align: "left",
            editable: false,
            sortable: false,
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            align: "center",
            renderCell: (params) => {
                const row = params.row as VehicleChecklistRow;
                return (
                    <div className="flex gap-2 items-center">
                        {row.filePath && (
                            <>
                                <EmailIcon
                                    className="cursor-pointer text-[#1976d2]"
                                    titleAccess="Send Email"
                                    onClick={() => {
                                        setFileNameForEmail(row.filePath || "");
                                        setOpenSendEmailPopup(true);
                                    }}
                                />
                                <DownloadIcon
                                    className="cursor-pointer text-[#388e3c]"
                                    titleAccess="Download"
                                    onClick={() => handleDownload(row.filePath)}
                                />
                                <DeleteIcon
                                    className="cursor-pointer text-[#d32f2f]"
                                    titleAccess="Delete"
                                    onClick={() => handleDelete(row)}
                                />
                            </>
                        )}
                        <DownloadIcon
                            className="text-[#f57c00] rotate-180 cursor-pointer"
                            titleAccess={row.filePath ? "Replace File" : "Upload File"}
                            onClick={() => {
                                document.getElementById(`upload-file-${row.id}`)?.click();
                            }}
                        />
                        <input
                            id={`upload-file-${row.id}`}
                            type="file"
                            accept=".png, .jpg, .jpeg, .pdf"
                            style={{ display: "none" }}
                            onChange={(e) => handleUploadFile(e, row)}
                        />
                    </div>
                );
            },
        },
    ];

    const fetchData = () => {
        if (!idNumber) return;

        setLoading(true);
        axios
            .get(`${baseURL}vehicleCheckList/GetVehicleCheckListsByVehicle/${idNumber?.state}`, {
                headers: {
                    'X-Tenant-ID': tenantId,
                }
            })
            .then((res) => {
                const formatted = res?.data?.reverse()?.map((item: any) => ({
                    id: item.id,
                    checklistName: item.checkListName ?? item.checklistName,
                    folder: item.folder || "Paperwork",
                    filePath: item.fileName || "",
                    isRequired: item.isRequired ?? false,
                    isUploaded: item.isUploaded ?? false,
                    uploadedBy: item.uploadedBy ?? "-",
                    uploadedAt: item.uploadedAt
                        ? moment(new Date(item.uploadedAt)).format(dateFormat + " hh:mm a")
                        : "-",
                    expirationDate:
                        item.fileName && item.expirationDate
                            ? moment(new Date(item.expirationDate)).format(dateFormat)
                            : "-",
                    tags: item.tags ?? "",
                    description: item.description ?? "",
                }));
                setTableData(formatted);
                handleTenantError(null);
            })
            .catch((err) => handleTenantError(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber, isFilesRefresh]);

    const handleUploadFile = (
        e: React.ChangeEvent<HTMLInputElement>,
        row: VehicleChecklistRow
    ) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPleaseWaitPopup(true);
        const formData = new FormData();
        formData.append("ImageFile", file);

        axios
            .post(`${baseURL}vehicleFile/upload`, formData, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "multipart/form-data"
                },
                withCredentials: true,
            })
            .then((res) => {
                const fileNameFromResponse = res?.data || file.name;
                setPleaseWaitPopup(false);
                setUploadedFileName(fileNameFromResponse);
                setPreviewFilePath(fileNameFromResponse);
                setSelectedRow(row);
                setExpirationDialogOpen(true);
                handleTenantError(null);
            })
            .catch((err) => {
                handleTenantError(err);
                setPleaseWaitPopup(false);
            });
    };

    const handleExpirationSubmit = () => {
        if (!selectedRow) return;

        const updatePayload = {
            id: selectedRow.id,
            vehicleId: idNumber.state,
            checkListName: selectedRow.checklistName,
            folder: "",
            fileName: uploadedFileName,
            isRequired: true,
            isUploaded: true,
            expirationDate: expirationDateInput,
            tags: "",
            description: "",
            createdAt: new Date().toISOString(),
            createdBy: auth?.givenname,
            updatedAt: new Date().toISOString(),
            updatedBy: auth?.givenname,
        };

        axios
            .put(`${baseURL}vehicleCheckList?userId=${auth?.id}`, updatePayload, {
                headers: {
                    'X-Tenant-ID': tenantId,
                },
                withCredentials: true,
            })
            .then(() => {
                setIsFileRefresh((prev) => !prev);
                handleTenantError(null);
            })
            .catch((err) => {
                handleTenantError(err);
            })
            .finally(() => {
                setPleaseWaitPopup(false);
                setExpirationDialogOpen(false);
                setExpirationDateInput("");
                setSelectedRow(null);
            });
    };

    const okCount = tableData.filter(
        (item) =>
            item.isRequired &&
            item.isUploaded &&
            item.expirationDate &&
            moment(item.expirationDate, dateFormat).isSameOrAfter(moment(), "day")
    ).length;

    const notOkCount = tableData.filter(
        (item) =>
            item.isRequired &&
            (!item.isUploaded ||
                !item.expirationDate ||
                moment(item.expirationDate, dateFormat).isBefore(moment(), "day"))
    ).length;

    const notRequiredCount = tableData.filter((item) => !item.isRequired).length;

    const total = tableData.length || 1;

    const okPercent = Math.round((okCount / total) * 100);
    const notOkPercent = Math.round((notOkCount / total) * 100);
    const notRequiredPercent = Math.round((notRequiredCount / total) * 100);

    const handleDelete = (row: VehicleChecklistRow) => {
        setData({
            title: "Delete file",
            summary: "Are you sure you want to delete this file?",
            confirmBtn: "Yes",
            isDelete: (cb: any) => {
                const updatePayload = {
                    id: row.id,
                    vehicleId: idNumber.state,
                    checkListName: row.checklistName,
                    folder: "",
                    fileName: "",
                    isRequired: true,
                    isUploaded: false,
                    expirationDate: "",
                    tags: "",
                    description: "",
                    createdAt: new Date().toISOString(),
                    createdBy: auth?.givenname,
                    updatedAt: new Date().toISOString(),
                    updatedBy: auth?.givenname,
                };

                axios
                    .put(`${baseURL}vehicleCheckList?userId=${auth?.id}`, updatePayload, {
                        headers: {
                            'X-Tenant-ID': tenantId,
                        },
                        withCredentials: true,
                    })
                    .then(() => {
                        setIsFileRefresh((prev) => !prev)
                        handleTenantError(null);
                    })
                    .catch((err) => {
                        handleTenantError(err);
                    })
                    .finally(cb);
            },
        });
        setDeletePopup(true);
    };

    const handleDownload = (fileName?: string) => {
        if (!fileName) return;
        const link = document.createElement("a");
        link.href = `${baseURL}vehicleFile/download?name=${fileName}`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <>
            <div className="w-full mt-2 flex flex-row gap-4 items-start">
                <div className="w-[78%] border rounded-[16px] border-gray-300">
                    <div
                        className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}
                    >
                        <h2
                            className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}
                        >
                            Checklist Files
                        </h2>
                    </div>
                    <CustomDataGrid
                        filteredRows={tableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: themeStyles.table.headerBg,
                        }))}
                        loading={loading}
                    />
                </div>

                <div className="w-[22%] border rounded-[16px] border-gray-300 p-4">
                    <h2 className={`text-md font-semibold mb-3 ${themeStyles.main.textHeadingColor}`}>Summary</h2>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col items-center p-3 rounded-lg bg-green-100 shadow-sm">
                            <span className="text-green-700 text-xl font-bold">👍 {okPercent}%</span>
                            <span className="text-sm font-medium text-green-800 mt-1">OK</span>
                        </div>
                        <div className="flex flex-col items-center p-3 rounded-lg bg-yellow-100 shadow-sm">
                            <span className="text-yellow-700 text-xl font-bold">👍 {notRequiredPercent}%</span>
                            <span className="text-sm font-medium text-yellow-800 mt-1">Not Required</span>
                        </div>
                        <div className="flex flex-col items-center p-3 rounded-lg bg-red-100 shadow-sm">
                            <span className="text-red-700 text-xl font-bold">👎 {notOkPercent}%</span>
                            <span className="text-sm font-medium text-red-800 mt-1">Not OK</span>
                        </div>
                    </div>
                </div>
            </div>

            <SendEmailChecklist
                fileNameForEmail={fileNameForEmail}
                openAddSendEmailChecklist={openSendEmailPopup}
                setOpenAddSendEmailChecklist={setOpenSendEmailPopup}
                setRefreshSendEmailChecklist={setIsFileRefresh}
            />

            <Dialog open={expirationDialogOpen}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        padding: '20px',
                        overflowY: "unset",
                    }
                }} onClose={() => setExpirationDialogOpen(false)}>
                <DialogTitle>Set Expiration Date</DialogTitle>
                <DialogContent>
                    <div className="space-y-4">
                        <div>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expiry Date</label>
                            <CustomFormatDatePicker
                                value={expirationDateInput}
                                onChange={setExpirationDateInput}
                                dateFormat={dateFormat}
                                required={true}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{
                            backgroundColor: colorConfigs.btn.main.bg
                        }}
                        variant="contained"
                        onClick={handleExpirationSubmit}
                    >
                        Save
                    </Button>

                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: colorConfigs.btn.cancel.bg,
                            border: colorConfigs.btn.cancel.border,
                            color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => setExpirationDialogOpen(false)}
                    >
                        Cancel
                    </Button>

                </DialogActions>
            </Dialog>

            <PreviewPopup
                showPopup={imagePopup}
                setShowPopup={setImagePopup}
                fileName={previewFilePath}
                setFileName={setPreviewFilePath}
                vehicleId={idNumber.state}
                checklistId={selectedRow?.id ?? 0}
                checkListName={selectedRow?.checklistName ?? ""}
                isRequired={selectedRow?.isRequired ?? false}
                isUploaded={selectedRow?.isUploaded ?? false}
                expirationDate={selectedRow?.expirationDate ?? ""}
                tags={selectedRow?.tags ?? ""}
                description={selectedRow?.description ?? ""}
                onUploadSuccess={() => setIsFileRefresh(prev => !prev)}
            />

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    );
};
